import { z } from 'zod'
import { iconSchema, idSchema, internalPathSchema, linkSchema } from './common'

export const messageSeveritySchema = z.enum([
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
  'critical',
])
export const messageOriginSchema = z.enum(['yaml', 'external', 'api'])
export const messageStatusSchema = z.enum(['scheduled', 'active', 'expired', 'disabled'])
export const isoDateTimeSchema = z.string().datetime({ offset: true })
export const messagePagesSchema = z
  .array(z.union([z.literal('*'), internalPathSchema]))
  .min(1)
  .max(20)
  .refine((pages) => new Set(pages).size === pages.length, 'les pages doivent être uniques')
  .refine(
    (pages) => !pages.includes('*') || pages.length === 1,
    'la page * ne peut pas être combinée à une autre page',
  )

const canonicalFields = {
  title: z.string().trim().min(1).max(120),
  content: z.string().min(1).max(20_000),
  author: z.string().trim().min(1).max(100).optional(),
  severity: messageSeveritySchema.default('info'),
  startAt: isoDateTimeSchema.optional(),
  endAt: isoDateTimeSchema.optional(),
  enabled: z.boolean().default(true),
  pages: messagePagesSchema.default(['*']),
  priority: z.number().int().min(0).max(100).default(50),
  dismissible: z.boolean().optional(),
  icon: iconSchema.optional(),
  link: linkSchema.optional(),
}

function validateDates(message: { startAt?: string; endAt?: string }, context: z.RefinementCtx) {
  if (
    message.startAt &&
    message.endAt &&
    Date.parse(message.startAt) >= Date.parse(message.endAt)
  ) {
    context.addIssue({
      code: 'custom',
      path: ['endAt'],
      message: 'la date de début doit précéder la date de fin',
    })
  }
}

const messageFieldsSchema = z.object(canonicalFields).strict()
export const messageInputSchema = messageFieldsSchema.superRefine(validateDates)

export const createMessageSchema = z
  .object({
    id: idSchema.optional(),
    ...canonicalFields,
  })
  .strict()
  .superRefine(validateDates)

export const updateMessageSchema = z
  .object({
    title: canonicalFields.title.optional(),
    content: canonicalFields.content.optional(),
    author: canonicalFields.author,
    severity: messageSeveritySchema.optional(),
    startAt: isoDateTimeSchema.optional(),
    endAt: isoDateTimeSchema.optional(),
    enabled: z.boolean().optional(),
    pages: messagePagesSchema.optional(),
    priority: z.number().int().min(0).max(100).optional(),
    dismissible: z.boolean().optional(),
    icon: iconSchema.optional(),
    link: linkSchema.optional(),
    revision: z.number().int().positive().optional(),
  })
  .strict()
  .refine(
    (value) => Object.keys(value).some((key) => key !== 'revision'),
    'au moins un champ doit être modifié',
  )

const storedMessageFieldsSchema = z
  .object({
    id: idSchema,
    ...canonicalFields,
    dismissible: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    revision: z.number().int().positive(),
  })
  .strict()
export const storedMessageSchema = storedMessageFieldsSchema.superRefine(validateDates)

export const messageStoreSchema = z
  .object({
    version: z.literal(1),
    messages: z.array(storedMessageSchema).max(1000),
  })
  .strict()

export const publicMessageSchema = storedMessageFieldsSchema
  .omit({ content: true })
  .extend({
    contentHtml: z.string().max(100_000),
    origin: messageOriginSchema,
    status: messageStatusSchema,
    readonly: z.boolean().optional(),
    legacyLevel: messageSeveritySchema.optional(),
  })
  .strict()

export const publicMessagesResponseSchema = z
  .object({
    messages: z.array(publicMessageSchema),
    generatedAt: isoDateTimeSchema,
    nextChangeAt: isoDateTimeSchema.nullable(),
  })
  .strict()

export const adminMessageSchema = storedMessageFieldsSchema
  .extend({
    origin: messageOriginSchema,
    status: messageStatusSchema,
    readonly: z.boolean(),
  })
  .strict()

export type MessageSeverity = z.infer<typeof messageSeveritySchema>
export type MessageStatus = z.infer<typeof messageStatusSchema>
export type MessageOrigin = z.infer<typeof messageOriginSchema>
export type MessageInput = z.infer<typeof messageInputSchema>
export type CreateMessageInput = z.input<typeof createMessageSchema>
export type UpdateMessageInput = z.input<typeof updateMessageSchema>
export type StoredMessage = z.infer<typeof storedMessageSchema>
export type PublicMessage = z.infer<typeof publicMessageSchema>
export type PublicMessagesResponse = z.infer<typeof publicMessagesResponseSchema>
export type AdminMessage = z.infer<typeof adminMessageSchema>
